import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, DollarSign, Clock, RotateCcw, MessageCircle, AlertTriangle, Tag } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import { formatPrice } from "../../lib/utils";
import GoLive from "../../components/business/GoLive";

const STATUS_LABEL = {
  draft: "Not live yet",
  pending: "In review",
  needs_info: "More information needed",
  approved: "Live",
  suspended: "Suspended",
  rejected: "Not accepted",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold text-slate-900">{value}</p>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = () =>
    api
      .get("/owner/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => setLoadError(true));

  useEffect(() => {
    load();
  }, []);

  if (loadError) {
    return <p className="text-sm text-destructive">Couldn't load your dashboard. Please refresh the page.</p>;
  }
  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-montserrat text-2xl font-bold text-slate-900">{data.business.name}</h1>
          <Badge variant={data.business.status === "approved" ? "accent" : "default"}>
            {STATUS_LABEL[data.business.status] || data.business.status}
          </Badge>
        </div>
      </div>

      <GoLive onChanged={load} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard icon={CalendarClock} label="Today's appointments" value={data.today_appointments.length} />
        <StatCard icon={DollarSign} label="Today's revenue" value={formatPrice(data.today_revenue)} />
        <StatCard icon={Clock} label="Pending requests" value={data.pending_count} />
        <StatCard icon={RotateCcw} label="Refund requests" value={data.pending_refunds} />
        <StatCard icon={MessageCircle} label="Unread messages" value={data.unread_messages} />
      </div>

      {data.low_stock_products?.length > 0 && (
        <Card className="mt-6 flex items-start gap-3 border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Low stock</p>
            <p className="text-sm text-amber-800">
              {data.low_stock_products.map((p) => p.name).join(", ")} running low.{" "}
              <Link to="/business/inventory" className="font-medium underline">Restock</Link>
            </p>
          </div>
        </Card>
      )}

      {data.active_promotions?.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 flex items-center gap-1 font-montserrat text-sm font-bold text-slate-900">
            <Tag className="h-4 w-4 text-primary" /> Promotions live right now
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.active_promotions.map((p) => (
              <Badge key={p.id} variant="warning">{p.name} — {p.discount_percent}% off</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-montserrat text-lg font-bold text-slate-900">Today's schedule</h2>
          <Link to="/business/calendar" className="text-sm font-medium text-primary">
            View calendar
          </Link>
        </div>
        {data.today_appointments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No appointments booked for today yet.</p>
        ) : (
          <div className="space-y-2">
            {data.today_appointments.map((a) => (
              <Card key={a.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {a.start_time?.slice(0, 5)} — {a.customer?.full_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {a.services?.map((s) => s.name_snapshot).join(", ")}
                    {a.staff?.name ? ` · ${a.staff.name}` : ""}
                  </p>
                </div>
                <Badge>{a.status}</Badge>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
